import * as React from "react";
import { RefObject, useEffect, useState } from "react";
import { minMax } from "conference/tools";

type Point = { x: number; y: number };

type Transform = {
    scale: number;
    translate: Point;
};

export const useZoom = (targetRef: RefObject<HTMLElement>, zoomFactor = 1.2) => {
    const [panStart, setPanStart] = useState<Point>();
    const [transform, setTransform] = useState<Transform>({
        scale: 1,
        translate: { x: 0, y: 0 },
    });

    const parentRect = targetRef.current?.parentElement.getBoundingClientRect();

    const updateTransform = (newTransform: Transform) => {
        const targetRect = targetRef.current.getBoundingClientRect();
        const scaleRatio = transform.scale / newTransform.scale;

        newTransform.translate.x = minMax(
            newTransform.translate.x,
            parentRect.width - targetRect.width / scaleRatio,
            0,
        );
        newTransform.translate.y = minMax(
            newTransform.translate.y,
            parentRect.height - targetRect.height / scaleRatio,
            0,
        );

        setTransform(newTransform);
    };

    useEffect(() => {
        const globalMouseUp = () => {
            setPanStart(null);
        };
        window.addEventListener("mouseup", globalMouseUp);
        return () => window.removeEventListener("mouseup", globalMouseUp);
    }, []);

    const onWheel = (ev: React.WheelEvent<HTMLElement>) => {
        const zoomPoint: Point = {
            x: ev.clientX - parentRect.x,
            y: ev.clientY - parentRect.y,
        };
        const zoomTarget: Point = {
            x: (zoomPoint.x - transform.translate.x) / transform.scale,
            y: (zoomPoint.y - transform.translate.y) / transform.scale,
        };

        const delta = -ev.deltaY;

        const scale = Math.max(delta > 0 ? transform.scale * zoomFactor : transform.scale / zoomFactor, 1);

        updateTransform({
            scale,
            translate: {
                x: zoomPoint.x - zoomTarget.x * scale,
                y: zoomPoint.y - zoomTarget.y * scale,
            },
        });
    };

    const onMouseDown = (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setPanStart({
            x: ev.clientX - transform.translate.x,
            y: ev.clientY - transform.translate.y,
        });
    };

    const onMouseUp = (_ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setPanStart(null);
    };

    const onMouseMove = (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (!panStart) return;
        updateTransform({
            scale: transform.scale,
            translate: {
                x: ev.clientX - panStart.x,
                y: ev.clientY - panStart.y,
            },
        });
    };

    const zoomStyle: React.CSSProperties = {
        transform: `translate(${transform.translate.x}px, ${transform.translate.y}px) scale(${transform.scale})`,
        transformOrigin: `0 0`,
    };

    return {
        eventHandlers: { onWheel, onMouseDown, onMouseUp, onMouseMove },
        zoomStyle,
        transform,
    };
};
